import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { useAuth, API } from "@/context/AuthContext";
import { Check, Loader2, CreditCard, Sparkles, Infinity as InfinityIcon, Mail, Plus, Minus } from "lucide-react";
import { toast } from "sonner";

const PLAN_CARDS = [
    {
        id: "free",
        name: "Free",
        price: "$0",
        unit: "/mo",
        tagline: "Try it. No card required.",
        cap: "500 generations / month",
        users: "1 user",
        features: [
            "500 AI generations / month",
            "Cold Outreach Generator",
            "Coach Mode",
            "60-day follow-up library",
            "Objection library",
            "Chrome extension",
        ],
        ctaKind: "current_or_downgrade",
    },
    {
        id: "standard",
        name: "Standard",
        price: "$19",
        unit: "/mo",
        tagline: "For the solo closer.",
        cap: "5,000 generations / month",
        users: "1 user",
        features: [
            "5,000 AI generations / month",
            "Everything in Free",
            "Document uploads (PDF · DOCX · TXT)",
            "All 8 sales methodologies",
            "Email support",
        ],
        ctaKind: "checkout",
    },
    {
        id: "pro",
        name: "Pro",
        price: "$49",
        unit: "/mo",
        tagline: "For teams closing together.",
        cap: "Unlimited generations",
        users: "10 users included",
        features: [
            "Unlimited AI generations",
            "Up to 10 team members included",
            "Extra users: $8 / user / month",
            "Shared team workspace",
            "Team Admin console + analytics",
            "Priority chat support",
        ],
        highlight: true,
        ctaKind: "checkout",
        hasSeats: true,
    },
    {
        id: "enterprise",
        name: "Enterprise",
        price: "Custom",
        unit: "",
        tagline: "For the org that owns the pipeline.",
        cap: "Unlimited generations",
        users: "Unlimited users",
        features: [
            "Everything in Pro",
            "Unlimited team seats",
            "SSO (Google · Microsoft · Okta)",
            "Dedicated CSM + custom onboarding",
            "SOC 2 review + custom DPA",
            "99.9% uptime SLA",
        ],
        ctaKind: "contact",
    },
];

export default function Billing() {
    const { authHeaders } = useAuth();
    const [plan, setPlan] = useState(null);
    const [loading, setLoading] = useState(true);
    const [checkingOut, setCheckingOut] = useState(null);
    const [searchParams, setSearchParams] = useSearchParams();
    const [polling, setPolling] = useState(false);
    const [extraSeats, setExtraSeats] = useState(0);

    const loadPlan = useCallback(async () => {
        try {
            const res = await axios.get(`${API}/billing/plan`, { headers: authHeaders() });
            setPlan(res.data);
        } finally {
            setLoading(false);
        }
    }, [authHeaders]); // eslint-disable-line

    useEffect(() => { loadPlan(); }, [loadPlan]);

    useEffect(() => {
        const sessionId = searchParams.get("session_id");
        if (!sessionId) {
            if (searchParams.get("cancelled")) {
                toast.info("Checkout cancelled.");
                setSearchParams({}, { replace: true });
            }
            return;
        }
        let attempts = 0;
        const maxAttempts = 8;
        setPolling(true);
        const poll = async () => {
            attempts += 1;
            try {
                const res = await axios.get(`${API}/billing/checkout/status/${sessionId}`, { headers: authHeaders() });
                if (res.data.payment_status === "paid") {
                    toast.success("Payment confirmed. You're live.");
                    setPolling(false);
                    setSearchParams({}, { replace: true });
                    await loadPlan();
                    return;
                }
                if (res.data.status === "expired") {
                    toast.error("Checkout expired. Please try again.");
                    setPolling(false);
                    setSearchParams({}, { replace: true });
                    return;
                }
            } catch (e) { /* keep polling */ }
            if (attempts >= maxAttempts) {
                setPolling(false);
                toast.info("Still processing — refresh in a moment.");
                return;
            }
            setTimeout(poll, 2000);
        };
        poll();
    }, [searchParams]); // eslint-disable-line

    const checkout = async (planId) => {
        if (!plan?.is_owner) {
            toast.error("Only the team owner can manage billing.");
            return;
        }
        setCheckingOut(planId);
        try {
            const res = await axios.post(
                `${API}/billing/checkout`,
                {
                    origin_url: window.location.origin,
                    plan_id: planId,
                    extra_seats: planId === "pro" ? extraSeats : 0,
                },
                { headers: authHeaders() }
            );
            window.location.href = res.data.url;
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not start checkout");
            setCheckingOut(null);
        }
    };

    if (loading) return <div className="text-zinc-500" data-testid="billing-loading">Loading plan…</div>;

    const isPro = plan?.plan === "pro";
    const adminUnlimited = !!plan?.admin_unlimited;
    const paidUntilDate = plan?.paid_until ? new Date(plan.paid_until) : null;
    const currentTier = plan?.tier;
    const currentPlanKey = adminUnlimited ? "enterprise" : (isPro ? (currentTier || "pro") : "free");

    const proTotal = 49 + 8 * extraSeats;

    return (
        <div data-testid="billing-page">
            <div className="mb-8">
                <div className="text-xs font-mono uppercase tracking-[0.2em] text-signal mb-2">/ BILLING</div>
                <h1 className="font-heading font-black text-4xl md:text-5xl tracking-tight">Your plan.</h1>
                <p className="text-zinc-400 mt-2">Four plans. No surprises. Cancel anytime.</p>
            </div>

            {polling && (
                <div className="mb-6 p-4 bg-signal/10 border border-signal/30 rounded-md flex items-center gap-3" data-testid="polling-banner">
                    <Loader2 className="w-4 h-4 text-signal animate-spin" />
                    <span className="text-sm">Confirming your payment with Stripe…</span>
                </div>
            )}

            {/* Current status */}
            <div className="grid md:grid-cols-3 gap-4 mb-10">
                <div className="border border-zinc-900 bg-[#0c0c0c] p-6" data-testid="plan-status">
                    <div className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-2">Current plan</div>
                    <div className="flex items-center gap-2 mb-2">
                        <span className="font-heading font-black text-3xl capitalize">
                            {adminUnlimited ? "Unlimited" : (isPro ? (currentTier || "Pro") : "Free")}
                        </span>
                        {adminUnlimited && (
                            <span className="px-2 py-0.5 rounded text-xs bg-signal/10 text-signal border border-signal/30 font-mono inline-flex items-center gap-1">
                                <InfinityIcon className="w-3 h-3" /> ADMIN
                            </span>
                        )}
                        {!adminUnlimited && isPro && (
                            <span className="px-2 py-0.5 rounded text-xs bg-signal/10 text-signal border border-signal/30 font-mono">ACTIVE</span>
                        )}
                    </div>
                    {!adminUnlimited && isPro && paidUntilDate && (
                        <div className="text-xs text-zinc-500 font-mono">Renews on {paidUntilDate.toLocaleDateString()}</div>
                    )}
                    {adminUnlimited && (
                        <div className="text-xs text-zinc-500 font-mono">Granted by admin · no billing</div>
                    )}
                </div>
                <div className="border border-zinc-900 bg-[#0c0c0c] p-6">
                    <div className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-2">Seats</div>
                    <div className="font-heading font-black text-3xl">
                        {plan?.active_members}
                        <span className="text-zinc-500 text-xl"> / {adminUnlimited ? "∞" : plan?.paid_seats}</span>
                    </div>
                    <div className="text-xs text-zinc-500 mt-1">active / paid</div>
                </div>
                <div className="border border-zinc-900 bg-[#0c0c0c] p-6">
                    <div className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-2">This period</div>
                    <div className="font-heading font-black text-3xl">
                        {plan?.generations_this_period}
                        <span className="text-zinc-500 text-xl">{plan?.generation_limit ? ` / ${plan.generation_limit.toLocaleString()}` : " · ∞"}</span>
                    </div>
                    <div className="text-xs text-zinc-500 mt-1">generations used</div>
                </div>
            </div>

            {/* Plan cards */}
            {!plan?.is_owner ? (
                <div className="border border-zinc-900 bg-[#0c0c0c] p-8 text-center">
                    <p className="text-zinc-400">Billing is managed by your team owner.</p>
                </div>
            ) : (
                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="plan-cards">
                    {PLAN_CARDS.map((p) => {
                        const isCurrent = currentPlanKey === p.id;
                        const busy = checkingOut === p.id;
                        const displayPrice = p.id === "pro" ? `$${proTotal}` : p.price;

                        let button;
                        if (p.ctaKind === "contact") {
                            button = (
                                <a
                                    href="mailto:sales@saleswhisper.dev?subject=Enterprise%20plan%20enquiry"
                                    data-testid={`checkout-btn-${p.id}`}
                                    className="w-full text-center border border-zinc-700 hover:border-signal hover:bg-signal/10 text-white px-4 py-3 rounded-md text-sm font-semibold flex items-center justify-center gap-2 transition-colors"
                                >
                                    <Mail className="w-4 h-4" /> Contact Sales
                                </a>
                            );
                        } else if (p.id === "free") {
                            button = (
                                <button
                                    disabled
                                    data-testid={`checkout-btn-${p.id}`}
                                    className="w-full px-4 py-3 rounded-md text-sm font-semibold border border-zinc-800 text-zinc-500 cursor-not-allowed"
                                >
                                    {isCurrent ? "Current plan" : "Free tier"}
                                </button>
                            );
                        } else {
                            button = (
                                <button
                                    onClick={() => checkout(p.id)}
                                    disabled={!!checkingOut || adminUnlimited || isCurrent}
                                    data-testid={`checkout-btn-${p.id}`}
                                    className={`w-full px-4 py-3 rounded-md text-sm font-semibold flex items-center justify-center gap-2 transition-colors disabled:opacity-60 disabled:cursor-not-allowed ${
                                        p.highlight
                                            ? "bg-signal hover:bg-signal-hover text-white"
                                            : "border border-zinc-700 hover:border-signal hover:bg-signal/10 text-white"
                                    }`}
                                >
                                    {busy ? (
                                        <><Loader2 className="w-4 h-4 animate-spin" /> Redirecting…</>
                                    ) : adminUnlimited ? (
                                        <><Sparkles className="w-4 h-4" /> Unlimited (admin)</>
                                    ) : isCurrent ? (
                                        <>Current plan</>
                                    ) : (
                                        <><CreditCard className="w-4 h-4" /> Subscribe to {p.name}</>
                                    )}
                                </button>
                            );
                        }

                        return (
                            <div
                                key={p.id}
                                data-testid={`plan-card-${p.id}`}
                                className={`relative border p-6 flex flex-col transition-colors ${
                                    p.highlight ? "border-signal bg-signal/5" : "border-zinc-900 bg-[#0c0c0c]"
                                }`}
                            >
                                {p.highlight && (
                                    <div className="absolute -top-3 left-6 px-2 py-0.5 bg-signal text-white text-[10px] font-mono uppercase tracking-widest rounded">
                                        Most Popular
                                    </div>
                                )}

                                <div className="flex items-center gap-2 mb-1">
                                    <h2 className="font-heading font-black text-2xl">{p.name}</h2>
                                    {isCurrent && (
                                        <span className="px-2 py-0.5 rounded text-[10px] bg-signal/10 text-signal border border-signal/30 font-mono uppercase tracking-widest">
                                            Current
                                        </span>
                                    )}
                                </div>
                                <p className="text-xs text-zinc-400 mb-4">{p.tagline}</p>

                                <div className="flex items-baseline gap-1 mb-1">
                                    <span className="font-heading font-black text-4xl" data-testid={`plan-price-${p.id}`}>{displayPrice}</span>
                                    {p.unit && <span className="text-xs text-zinc-500 font-mono">{p.unit}</span>}
                                </div>
                                <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-4 space-y-0.5" data-testid={`plan-cap-${p.id}`}>
                                    <div>{p.cap}</div>
                                    <div className="text-zinc-600" data-testid={`plan-seats-${p.id}`}>{p.users}</div>
                                </div>

                                {p.hasSeats && (
                                    <div className="border border-zinc-800 bg-zinc-950 rounded-md p-3 mb-4" data-testid="pro-seats-stepper">
                                        <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-2">Extra users (+$8/mo each)</div>
                                        <div className="flex items-center justify-between">
                                            <button
                                                onClick={() => setExtraSeats(s => Math.max(0, s - 1))}
                                                disabled={extraSeats <= 0}
                                                data-testid="extra-seats-decrement"
                                                className="w-8 h-8 border border-zinc-800 hover:border-zinc-600 rounded flex items-center justify-center disabled:opacity-40"
                                            >
                                                <Minus className="w-3 h-3" />
                                            </button>
                                            <span className="font-mono font-bold text-lg" data-testid="extra-seats-value">+{extraSeats}</span>
                                            <button
                                                onClick={() => setExtraSeats(s => Math.min(490, s + 1))}
                                                data-testid="extra-seats-increment"
                                                className="w-8 h-8 border border-zinc-800 hover:border-zinc-600 rounded flex items-center justify-center"
                                            >
                                                <Plus className="w-3 h-3" />
                                            </button>
                                        </div>
                                        <div className="text-[10px] text-zinc-600 font-mono text-center mt-2">
                                            Total: {10 + extraSeats} users · ${proTotal}/mo
                                        </div>
                                    </div>
                                )}

                                <ul className="space-y-2 text-xs text-zinc-300 mb-5 flex-1">
                                    {p.features.map((f, i) => (
                                        <li key={i} className="flex items-start gap-2">
                                            <Check className="w-3.5 h-3.5 text-signal mt-0.5 flex-shrink-0" strokeWidth={2.5} />
                                            <span>{f}</span>
                                        </li>
                                    ))}
                                </ul>

                                {button}
                            </div>
                        );
                    })}
                </div>
            )}

            <div className="mt-10 text-[10px] text-zinc-600 text-center font-mono uppercase tracking-widest">
                Powered by Stripe · Test mode
            </div>
            <div className="mt-2 text-xs text-zinc-500 text-center">
                Need to add teammates? Head to <Link to="/app/team" className="text-signal hover:underline" data-testid="billing-team-link">Team</Link>.
            </div>
        </div>
    );
}
